import { useState, useEffect, useCallback, type FC } from 'react'
import { Box, Fade, DialogContent, DialogContentText, DialogActions, Paper, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { Body2, Button, H5, IconButton } from '@dhl-ui/components'
import { Close } from '@dhl/icons'
import { useWindowSize } from '@dhl/use-window'

import { defaultPopperConfig, type Step } from './model.js'
import { Arrow, Popper } from './Popper.js'
import { guidedTourClasses } from './classes.js'
import { type FocusBackdropDimensions } from './FocusBackdrop.js'

type Props = {
  documentScrollQuerySelector?: string
  progress?: string
  step: Step
  tourName: string
  onBack?(): void
  onClose?(): void
  onNext?(): void
  showCloseIconButton?: boolean
  onFocusDimensionsChange(dimensions: FocusBackdropDimensions): void
}

const StepPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(49, 49, 49)' : theme.palette.common.white,
  backgroundImage: 'unset',
}))

const DialogActionButtonContainer = styled(Box)({
  flexBasis: '50%',
  marginLeft: 'auto',
  width: '100%',
})

export const DisplayStep: FC<Props> = ({
  documentScrollQuerySelector,
  progress,
  step,
  tourName,
  onBack,
  onClose,
  onNext,
  showCloseIconButton,
  onFocusDimensionsChange,
}) => {
  const [arrowRef, setArrowRef] = useState<HTMLDivElement | null>(null)
  const { t } = useTranslation()
  const { width: windowWidth, height: windowHeight } = useWindowSize()
  const popperId = 'guided-tour-popper'

  const { body, highlightElement, target, title, popperConfig: suppliedPopperConfig, onHide, onShow } = step

  const popperConfig = suppliedPopperConfig
    ? {
        ...defaultPopperConfig,
        ...suppliedPopperConfig,
      }
    : defaultPopperConfig

  const { disablePortal, placement, sx, transitionTimeout, offset, arrowOptions = {} } = popperConfig

  const getElementScrollTop = useCallback(
    (element: HTMLElement) => {
      const { height, y: elementY } = element.getBoundingClientRect()
      const popperHeight = document.querySelector(`#${popperId}`)?.getBoundingClientRect()?.height ?? 0

      const totalHeight = elementY + height + popperHeight
      const additionalVerticalPadding = 16

      if (totalHeight > windowHeight) {
        const totalHeightWithMargin = totalHeight + additionalVerticalPadding

        return totalHeightWithMargin - windowHeight
      }
      return undefined
    },
    [windowHeight]
  )

  useEffect(() => {
    if (highlightElement) {
      const element = document.querySelector(highlightElement) as HTMLElement

      if (!element) {
        return
      }

      const maxRetryElementBoundingClientReact = 20
      let currentRetryElementBoundingClientReact = 1

      const watchForElementBoundingClientReact = setInterval(() => {
        const { top, left, height, width } = element.getBoundingClientRect()

        if ((width > 0 && height > 0) || currentRetryElementBoundingClientReact > maxRetryElementBoundingClientReact) {
          if (documentScrollQuerySelector) {
            document.querySelector(documentScrollQuerySelector)?.scrollTo({ top: getElementScrollTop(element) })
          }

          onFocusDimensionsChange({
            top,
            left,
            height,
            width,
          })

          clearInterval(watchForElementBoundingClientReact)
        }

        currentRetryElementBoundingClientReact += 1
      }, 100)
    }
  }, [documentScrollQuerySelector, getElementScrollTop, highlightElement, onFocusDimensionsChange, windowWidth])

  useEffect(() => {
    onShow?.()

    return () => onHide?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Popper
      open
      transition
      anchorEl={document.querySelector(target)}
      disablePortal={disablePortal}
      id={`${popperId}-${tourName}`}
      modifiers={[
        {
          name: 'flip',
          enabled: true,
          options: {
            altBoundary: true,
            rootBoundary: 'document',
            padding: 8,
          },
        },
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            escapeWithReference: true,
            boundariesElement: 'viewport',
          },
        },
        {
          name: 'arrow',
          enabled: true,
          options: {
            ...arrowOptions,
            element: arrowRef,
          },
        },
        {
          name: 'offset',
          enabled: true,
          options: {
            offset,
          },
        },
      ]}
      placement={placement}
      sx={sx}
    >
      {({ TransitionProps: transitionProps }) => (
        <Fade {...transitionProps} timeout={transitionTimeout}>
          <Box className={guidedTourClasses.step} mx={2}>
            <Arrow ref={setArrowRef} className={guidedTourClasses.arrow} />
            <Box>
              <StepPaper>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  px={2}
                  py={1}
                  width="100%"
                  minHeight={56}
                >
                  <Box className={guidedTourClasses.title}>
                    <H5>{title}</H5>
                  </Box>

                  <Box
                    display="flex"
                    alignItems="center"
                    flexBasis="50%"
                    justifyContent="end"
                    mr={showCloseIconButton ? -1 : 0}
                  >
                    {progress && (
                      <Box className={guidedTourClasses.progress}>
                        <Body2 variant="tertiary">{progress}</Body2>
                      </Box>
                    )}

                    {showCloseIconButton && (
                      <Box className={guidedTourClasses.closeButton}>
                        <IconButton
                          aria-label={t('guidedTour.step.closeButton')}
                          data-test="guided-tour-close-button"
                          onClick={onClose}
                        >
                          <Close />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Box>

                <DialogContent>
                  <DialogContentText className={guidedTourClasses.body}>{body}</DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between' }}>
                  {onBack && (
                    <DialogActionButtonContainer className={guidedTourClasses.backButton}>
                      <Button
                        fullWidth
                        color="dark"
                        data-test="guided-tour-back-button"
                        variant="text"
                        onClick={onBack}
                      >
                        {t('guidedTour.step.backButton')}
                      </Button>
                    </DialogActionButtonContainer>
                  )}
                  {onNext && (
                    <DialogActionButtonContainer className={guidedTourClasses.nextButton}>
                      <Button fullWidth variant="contained" data-test="guided-tour-next-button" onClick={onNext}>
                        {t('guidedTour.step.nextButton')}
                      </Button>
                    </DialogActionButtonContainer>
                  )}
                  {!onNext && (
                    <DialogActionButtonContainer className={guidedTourClasses.finishButton}>
                      <Button fullWidth variant="contained" data-test="guided-tour-finish-button" onClick={onClose}>
                        {t('guidedTour.step.finishButton')}
                      </Button>
                    </DialogActionButtonContainer>
                  )}
                </DialogActions>
              </StepPaper>
            </Box>
          </Box>
        </Fade>
      )}
    </Popper>
  )
}
